import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { useTabDrafts } from "@/components/ui/box-draft";
import type { Blocker, Result, TaskBox } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { reasonOnError } from "@/lib/api/commitSeams";
import { inboxApi, type InboxChannel, type NewConversation } from "@/lib/api/inbox";
import { inboxRead } from "./inbox-read";
import { inboxWords as words } from "./inbox-copy";
import type { PendingReply, SendDecision } from "./use-conversation-replies";

interface MessageDraft { channel: InboxChannel; course: string; recipient: string; session: string; subject: string; body: string }
export interface NewMessageTransport {
  send(request: NewConversation, key: string): Promise<SendDecision>;
  check(pending: PendingReply): Promise<SendDecision>;
}

export function useNewMessageBox({course="",recipient="",session="",transport,enabled=true}:{
  course?:string;recipient?:string;session?:string;transport:NewMessageTransport;enabled?:boolean;
}): {box:TaskBox<Awaited<ReturnType<typeof inboxApi.context>>>;reset():void} {
  const {me}=useAuth();
  const fieldPrefix=useId();
  const scope=`${course}/${recipient}/${session}`;
  const draftOf=useTabDrafts<MessageDraft,PendingReply>("new-message");
  const spec={record:scope,version:null,initial:{channel:"email" as InboxChannel,course,recipient,session,subject:"",body:""}};
  const draft=draftOf(spec);
  const value=draft.value;
  const context=useQuery({queryKey:["inbox",me?.tenant_id,me?.user_id,"context",value.course],
    queryFn:()=>inboxApi.context(value.course || undefined),enabled:Boolean(me)&&enabled,retry:false});
  const [results,setResults]=useState<Record<string,Result>>({});
  const result=results[scope]??{state:"none"};
  const setResult=(next:Result)=>setResults(was=>({...was,[scope]:next}));
  useEffect(()=>{setResults(was=>({...was,[scope]:{state:"none"}}));},[scope]);
  const inFlight=useRef(new Map<string,Promise<Result>>());
  const sending=useRef(new Set<string>());
  const unknown=(pending:PendingReply,sentence:string=words.sendUnknown):Result=>({state:"unknown",cap:"",requestId:pending.key,sentence,
    check:{verb:words.checkStatus,note:words.checkOnly,run:()=>exclusive(async()=>{
      try {return settle(await transport.check(pending),pending);} catch {const next=unknown(pending);setResult(next);return next;}
    })}});
  function exclusive(run:()=>Promise<Result>):Promise<Result> {
    const running=inFlight.current.get(scope);
    if(running)return running;
    const request=Promise.resolve().then(run).finally(()=>{inFlight.current.delete(scope);});
    inFlight.current.set(scope,request);
    return request;
  }
  function settle(decision:SendDecision,pending:PendingReply):Result {
    const next:Result=decision.state==="unknown" ? unknown(pending,decision.sentence)
      :decision.state==="confirmed"?{state:"confirmed",at:decision.at,effects:[words.messageAdded],remains:[]}
      :{state:"refused",cap:words.messageNotSent,sentence:decision.sentence,draft:"kept"};
    if(next.state!=="unknown"){draft.setPending(null);}
    setResult(next);
    return next;
  }
  const drawnResult=result.state==="none" && draft.pending && !sending.current.has(scope) ? unknown(draft.pending):result;
  const update=(patch:Partial<MessageDraft>)=>{
    draft.set(was=>({...was,...patch}));
    if(drawnResult.state!=="unknown")setResult({state:"none"});
  };
  const to="new-message-to",message="new-message-body";
  const person=context.data?.recipients.find(person=>person.user_id===value.recipient);
  const channel=context.data?.channels[value.channel];
  const ready=channel?.status==="active";
  const blockers=():Blocker[]=>[
    ...(!ready?[{kind:"missing" as const,part:to,sentence:words.channelNotReady}]:[]),
    ...(!value.course?[{kind:"missing" as const,part:to,sentence:words.chooseCourse}]:[]),
    ...(!person?[{kind:"missing" as const,part:to,sentence:words.choosePerson}]
      :person.availability && person.availability!=="available"?[{kind:"missing" as const,part:to,sentence:words.personUnavailable(person.name)}]:[]),
    ...(value.session && !context.data?.sessions.some(item=>item.id===value.session)?[{kind:"missing" as const,part:to,sentence:words.sessionUnavailable}]:[]),
    ...(value.channel==="email" && !value.subject.trim()?[{kind:"missing" as const,part:message,sentence:words.addSubject}]:[]),
    ...(!value.body.trim()?[{kind:"missing" as const,part:message,sentence:words.writeMessage}]:[]),
  ];
  const state=drawnResult.state==="unknown" ? {class:"reading" as const,word:words.messageUnknown}
    :drawnResult.state==="confirmed"?{class:"done" as const,word:words.messageConfirmed}:draft.kept?{class:"draft" as const,word:words.messageDraft}:{class:"quiet" as const,word:words.nothingStarted};
  const frozen=Boolean(draft.pending);
  const box:TaskBox<Awaited<ReturnType<typeof inboxApi.context>>> = {
    id:"new-message",kind:"task",label:words.newMessage,job:words.startPrivate,owner:{system:"imam"},
    read:inboxRead(context,words.contextFailed),state:()=>state,detail:()=>words.startPrivate,
    draft:draft.kept,result:drawnResult,act:{does:"open",verb:words.newMessage},fold:"choice",
    exits:drawnResult.state==="confirmed"?[{label:words.writeAnotherMessage,reopens:true}]:undefined,
    parts:[{
      id:to,label:words.to,owner:{system:"imam"},fold:"never",draft:"browser",
      state:{class:person?"done":"waiting",word:person?words.personChosen:words.chooseRecipient},
      render:()=> <div className="grid gap-4">
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-channel`}>{words.sendAs}</Label>
          <NativeSelect id={`${fieldPrefix}-message-channel`} disabled={frozen} value={value.channel} onChange={event=>update({channel:event.target.value as InboxChannel})}>
            <option value="email">{words.email}</option><option value="chat">{words.chat}</option>
          </NativeSelect></div>
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-course`}>{words.course}</Label>
          <NativeSelect id={`${fieldPrefix}-message-course`} disabled={frozen} value={value.course} onChange={event=>update({course:event.target.value,recipient:"",session:""})}>
            <option value="">{words.chooseCourseOption}</option>
            {context.data?.courses.map(item=><option key={item.id} value={item.id}>{item.title}</option>)}
          </NativeSelect></div>
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-person`}>{words.person}</Label>
          <NativeSelect id={`${fieldPrefix}-message-person`} disabled={frozen || !value.course || context.isFetching} value={value.recipient} onChange={event=>update({recipient:event.target.value})}>
            <option value="">{words.choosePersonOption}</option>
            {value.recipient && !person && <option value={value.recipient}>{words.recipientUnavailable}</option>}
            {context.data?.recipients.map(item=><option key={item.user_id} value={item.user_id}>{item.name}{item.availability && item.availability!=="available"?words.unavailableSuffix:""}</option>)}
          </NativeSelect></div>
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-session`}>{words.optionalSession}</Label>
          <NativeSelect id={`${fieldPrefix}-message-session`} disabled={frozen || !value.course} value={value.session} onChange={event=>update({session:event.target.value})}>
            <option value="">{words.wholeCourse}</option>
            {value.session && !context.data?.sessions.some(item=>item.id===value.session) && <option value={value.session}>{words.sessionUnavailable}</option>}
            {context.data?.sessions.map(item=><option key={item.id} value={item.id}>{item.title}</option>)}
          </NativeSelect></div>
      </div>,
    },{
      id:message,label:words.message,owner:{system:"imam"},fold:"never",draft:"browser",keep:draft,
      state:{class:value.body?"draft":"waiting",word:value.body?words.messageWritten:words.notWritten},
      render:()=> <div className="grid gap-4">
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-subject`}>{value.channel==="email"?words.subject:words.optionalTopic}</Label>
          <Input id={`${fieldPrefix}-message-subject`} maxLength={200} disabled={frozen} value={value.subject} onChange={event=>update({subject:event.target.value})} /></div>
        <div className="grid gap-2"><Label htmlFor={`${fieldPrefix}-message-text`}>{words.message}</Label>
          <Textarea id={`${fieldPrefix}-message-text`} maxLength={20000} disabled={frozen} value={value.body} onChange={event=>update({body:event.target.value})} /></div>
      </div>,
    }],
    commit:{verb:channel?.mode==="preview"?words.sendPreview:words.sendMessage,cap:words.whenYouSend,blockers,
      effects:()=>[{id:"message",sentence:person?words.startWith(person.name):words.startPrivate}],
      note:channel?.mode==="preview"?words.privatePreview:words.privateToTwo,
      run:key=>exclusive(async()=>{
        const unanswered=draftOf(spec).pending;
        if(unanswered)return unknown(unanswered);
        const blocked=blockers()[0];
        if(blocked && "sentence" in blocked)return settle({state:"refused",sentence:blocked.sentence},{key,operation:key,legacy:false});
        const pending={key,operation:key,legacy:false};
        sending.current.add(scope);
        draft.setPending(pending);
        try {return settle(await transport.send({course_id:value.course,recipient_user_id:value.recipient,session_id:value.session||null,
          channel:value.channel,subject:value.subject.trim(),body:value.body.trim(),operation_id:key},key),pending);}
        catch(error){return settle(error instanceof ApiError && error.status>=400 && error.status<500 && error.status!==408
          ?{state:"refused",sentence:reasonOnError(error)?.message??error.message}:{state:"unknown"},pending);}
        finally{sending.current.delete(scope);}
      }),
    },
  };
  return {box,reset:()=>{if(drawnResult.state!=="unknown")setResult({state:"none"});}};
}
